import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../components/auth/AuthProvider';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import { Checkbox } from '../../components/ui/checkbox';
import { motion } from 'framer-motion';
import {
  Mail, Lock, Eye, EyeOff, AlertCircle,
  Command, ArrowRight, Loader2,
  ShieldCheck
} from 'lucide-react';
import { FaGithub, FaGoogle } from 'react-icons/fa';
import { Icon } from '@/components/ui/icon';
import { Badge } from '@/components/ui/badge';
import AuthLeftPanel from './components/AuthLeftPanel';

const DEMO_CREDENTIAL = { email: 'admin@example.com', password: 'admin123' };

const Login = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [remember, setRemember] = useState(false);
  const { login } = useAuth();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(formData.email, formData.password);
    if (!result.success) setError(result.error);
    setLoading(false);
  };

  const fillDemo = () => {
    setFormData(DEMO_CREDENTIAL);
    setError('');
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* ── Left panel — decorative ─────────────── */}
      <AuthLeftPanel titlePrefix="The Admin Panel" titleSuffix="You Deserve." />

      {/* ── Right panel — login form ─────────────── */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-10 bg-background">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          className="w-full max-w-md"
        >
          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <Badge className="rounded-xl p-2">
              <Icon icon={Command} color="white" />
            </Badge>
            <span className="font-black text-lg tracking-tight">shadex</span>
          </div>

          <div className="mb-8">
            <h1 className="text-2xl font-bold tracking-tight">Welcome back</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Sign in to your admin account to continue
            </p>
          </div>

          {/* Error message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mb-5 p-3 rounded-lg bg-danger/10 border border-danger/25 flex items-center gap-2.5"
            >
              <Icon icon={AlertCircle} color="danger" size="sm" />
              <p className="text-sm text-danger">{error}</p>
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email */}
            <div className="space-y-1.5">
              <Label htmlFor="email">Email address</Label>
              <div className="relative">
                <Icon icon={Mail} className="absolute left-3 top-1/2 -translate-y-1/2" color="muted" size="sm" />
                <Input
                  id="email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="admin@example.com"
                  className="pl-9 h-10"
                  autoComplete="email"
                  required
                />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" className="text-xs text-primary hover:underline underline-offset-4">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Icon icon={Lock} className="absolute left-3 top-1/2 -translate-y-1/2" color="muted" size="sm" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="pl-9 pr-10 h-10"
                  autoComplete="current-password"
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setShowPassword(s => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  {showPassword ? <Icon icon={EyeOff} /> : <Icon icon={Eye} />}
                </Button>
              </div>
            </div>

            {/* Remember me */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="remember"
                checked={remember}
                onCheckedChange={(checked) => setRemember(checked === true)}
              />
              <Label htmlFor="remember" className="text-sm text-muted-foreground cursor-pointer">
                Remember me for 30 days
              </Label>
            </div>

            {/* Submit */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full gap-2"
            >
              {loading
                ? <><Icon icon={Loader2} className="animate-spin" color="white" /> Signing in…</>
                : <><span>Sign in</span><Icon icon={ArrowRight} color="white" /></>
              }
            </Button>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-3 my-5">
            <Separator className="flex-1" />
            <span className="text-xs text-muted-foreground">or continue with</span>
            <Separator className="flex-1" />
          </div>

          {/* Social buttons */}
          <div className="grid grid-cols-2 gap-3">
            <Button variant="outline" className="gap-2 text-sm" type="button">
              <FaGithub className="h-4 w-4" />
              GitHub
            </Button>
            <Button variant="outline" className="gap-2 text-sm" type="button">
              <FaGoogle className="h-4 w-4" />
              Google
            </Button>
          </div>

          {/* Register link */}
          <p className="mt-6 text-center text-sm text-muted-foreground">
            Don't have an account?{' '}
            <Link to="/register" className="font-semibold text-primary hover:underline underline-offset-4">
              Create account
            </Link>
          </p>

          {/* Demo credentials */}
          <motion.button
            type="button"
            onClick={fillDemo}
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            className="mt-4 w-full text-center text-xs text-muted-foreground/60 hover:text-muted-foreground transition-colors py-2 rounded-lg border border-dashed border-border hover:bg-muted/30"
          >
            🔑 Use demo credentials — <span className="font-mono">admin@example.com / admin123</span>
          </motion.button>
        </motion.div>
      </div>
    </div>
  );
};

export default Login;